import { useEffect, useRef, useState } from "react";

const MIN_SCALE = 2 / 3;

/**
 * Scale logos by how much they fill the box with object-contain:
 * high fill (e.g. ~3:2 in a wide slot) → closer to 2/3
 * low fill (very wide or very narrow) → closer to full size
 */
export function computeSponsorLogoScale(naturalW, naturalH, boxW, boxH, minScale = MIN_SCALE) {
    if (!naturalW || !naturalH || !boxW || !boxH) return 1;
    const aspect = naturalW / naturalH;
    const fittedW = Math.min(boxW, boxH * aspect);
    const fittedH = Math.min(boxH, boxW / aspect);
    const fill = Math.min(1, Math.max(0, (fittedW * fittedH) / (boxW * boxH)));
    return 1 - (1 - minScale) * fill;
}

function SponsorLogo({ src, alt = "sponsor", minScale = MIN_SCALE }) {
    const boxRef = useRef(null);
    const imgRef = useRef(null);
    const [natural, setNatural] = useState({ w: 0, h: 0 });
    const [scale, setScale] = useState(1);

    const applyNaturalSize = (img) => {
        if (img?.naturalWidth && img?.naturalHeight) {
            setNatural({ w: img.naturalWidth, h: img.naturalHeight });
        }
    };

    useEffect(() => {
        applyNaturalSize(imgRef.current);
    }, [src]);

    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;

        const update = () => {
            const { width, height } = el.getBoundingClientRect();
            setScale(computeSponsorLogoScale(natural.w, natural.h, width, height, minScale));
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [natural.w, natural.h, minScale]);

    return (
        <div ref={boxRef} className="w-full h-full min-h-0 min-w-0 flex items-center justify-center overflow-hidden">
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                className="object-contain max-w-full max-h-full w-auto h-auto"
                style={{
                    maxWidth: `${scale * 100}%`,
                    maxHeight: `${scale * 100}%`,
                }}
                onLoad={(e) => applyNaturalSize(e.currentTarget)}
            />
        </div>
    );
}

export default SponsorLogo;
